import { useEffect, useState } from "react";
import { FileSignature, Link2, CheckCircle2 } from "lucide-react";
import { apiClient, API } from "@/lib/api";
import { AdminAssistantChat } from "@/components/admin/AdminAssistantChat";
import { toast } from "sonner";

export const ContractManager = () => {
  const [contracts, setContracts] = useState(null);

  const fetchContracts = async () => {
    try {
      const { data } = await apiClient.get("/admin/contracts");
      setContracts(data);
    } catch {
      toast.error("Could not load contracts");
    }
  };

  useEffect(() => {
    fetchContracts();
  }, []);

  const handleAction = (actionType) => {
    if (actionType === "GENERATE_CONTRACT") {
      toast.success("Contract generated — see Recent Contracts below");
      fetchContracts();
    }
  };

  const viewPdf = async (id) => {
    try {
      const token = localStorage.getItem("ferguson_admin_token");
      const res = await fetch(`${API}/admin/contracts/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch {
      toast.error("Could not open contract PDF");
    }
  };

  const copySigningLink = (token) => {
    const url = `${window.location.origin}/sign-contract/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Signing link copied — share it with the client");
  };

  return (
    <div data-testid="admin-contract-manager" className="grid lg:grid-cols-[1.3fr_1fr] gap-6">
      <AdminAssistantChat
        mode="contract"
        greeting="Let's draft a new service contract. Who's the client, and what's their address?"
        placeholder="Type the client's details..."
        onAction={handleAction}
      />

      <div>
        <div className="text-xs text-[#6E7E74] uppercase tracking-wide mb-2">Recent Contracts</div>
        {!contracts ? (
          <p className="text-[#A1B0A6] text-sm">Loading...</p>
        ) : contracts.length === 0 ? (
          <p className="text-[#A1B0A6] text-sm" data-testid="contract-list-empty">No contracts generated yet.</p>
        ) : (
          <div className="space-y-2">
            {contracts.map((c) => (
              <div key={c.id} data-testid={`contract-row-${c.id}`} className="rounded-xl border border-gold/15 bg-deep-card p-4">
                <div className="flex items-start gap-3">
                  <FileSignature size={16} className="text-gold shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-semibold text-[#F4F7F5] text-sm">{c.client_name}</span>
                      {c.signature_status === "signed" ? (
                        <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-300 bg-emerald-500/15 px-2 py-0.5 rounded-full" data-testid={`contract-signed-badge-${c.id}`}>
                          <CheckCircle2 size={10} /> Signed
                        </span>
                      ) : (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-[#A1B0A6] bg-[#6E7E74]/20 px-2 py-0.5 rounded-full" data-testid={`contract-unsigned-badge-${c.id}`}>
                          Unsigned
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#6E7E74] mt-0.5">{c.service_type} · {c.trade_lane}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <button
                    onClick={() => viewPdf(c.id)}
                    data-testid={`contract-view-pdf-${c.id}`}
                    className="text-xs font-medium text-gold hover:text-gold-hover"
                  >
                    View Draft PDF
                  </button>
                  {c.signature_status !== "signed" && (
                    <button
                      onClick={() => copySigningLink(c.signature_token)}
                      data-testid={`contract-copy-signing-link-${c.id}`}
                      className="flex items-center gap-1 text-xs font-medium text-gold hover:text-gold-hover"
                    >
                      <Link2 size={12} /> Copy Signing Link
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
